import {Text} from '@mantine/core';
import React from 'react';

import {isGermlineCompatibleWithDomainTypes} from 'antibodies/germline/germlineCompatibility';
import {Germline, useGermlinesQuery} from 'antibodies/germline/useGermlinesQuery';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {CenteredSpinner} from 'ui-library/Spinner';
import {assertNever} from 'util/Types';

export const NO_COMPATIBLE_GERMLINES_TEXT = 'No germlines support this format';

export interface GermlineSelectorProps {
  selectedGermlineId: string | null;
  onSelectGermline: (germlineId: string) => void;
  disabled: boolean;
  displayName?: string;
  extraOptions?: Array<OptionData>;
  /** When set, only germlines that support all of these domain types are shown. */
  domainTypes?: ReadonlyArray<AntibodyDomainType>;
  /** Help text shown under the dropdown. */
  warning?: string | null;
}

function toGermlineOptionDatas(
  compatibleGermlines: Array<Germline>,
  domainTypes: ReadonlyArray<AntibodyDomainType> | undefined
): Array<OptionData> {
  if (domainTypes != null && compatibleGermlines.length === 0) {
    return [{name: NO_COMPATIBLE_GERMLINES_TEXT, disabled: true}];
  }
  return compatibleGermlines.map((germline) => ({name: germline.name, value: germline.id}));
}

function toOptionDatas(
  germlineOptions: Array<OptionData>,
  extraOptions: Array<OptionData>
): Array<OptionData> {
  // Extra options go at the top. When present, add a border on the first germline
  // option to visually separate the two groups.
  if (extraOptions.length > 0 && germlineOptions.length > 0) {
    germlineOptions[0] = {...germlineOptions[0], className: 'schema-section-divider'};
  }
  return [...extraOptions, ...germlineOptions];
}

export default function GermlineSelector(props: GermlineSelectorProps): JSX.Element {
  const {status, germlines} = useGermlinesQuery();

  switch (status) {
    case QueryResultType.PENDING:
      return <CenteredSpinner />;
    case QueryResultType.ERROR:
      return <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load germlines." />;
    case QueryResultType.SUCCESS: {
      const {domainTypes} = props;
      const compatibleGermlines =
        domainTypes == null
          ? germlines
          : germlines.filter((germline) => isGermlineCompatibleWithDomainTypes(germline, domainTypes));
      const selectedGermline =
        props.selectedGermlineId != null
          ? compatibleGermlines.find((g) => g.id === props.selectedGermlineId)
          : null;
      const displayName = props.displayName ?? selectedGermline?.name ?? 'Select germline';
      return (
        <>
          <SelectDropdown
            disabled={props.disabled}
            displayName={displayName}
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                onSelect={(value: OptionValue) => {
                  props.onSelectGermline(value as string);
                }}
                optionDatas={toOptionDatas(
                  toGermlineOptionDatas(compatibleGermlines, domainTypes),
                  props.extraOptions ?? []
                )}
                selectedValue={props.selectedGermlineId}
              />
            }
          />
          {props.warning != null && (
            <Text color="dimmed" size="sm" fs="italic" mt="xs">
              {props.warning}
            </Text>
          )}
        </>
      );
    }
    default:
      return assertNever(status);
  }
}
